import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';

import CreateEquipment from '../../Equipment/CreateEquipment/CreateEquipment';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';
import StringUtils from '../../../utils/stringUtils';
import EditDriver from '../../Drivers/EditDriver/EditDriver';
import useAuth from '../../../providers/Auth/Auth.provider';
import CreateDriver from '../../Drivers/CreateDriver/CreateDriver';
import EditEquipment from '../../Equipment/EditEquipment/EditEquipment';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import LoadsService from '../../../providers/Loads/Loads.service';
import TwDriverPayCalculator from '../../../components/TwDriverPayCalc/TwDriverPayCalculator';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import DateUtils from '../../../utils/dateUtils';
import { CarrierResponse } from '../../../redux/api/carriers/carriers.model';
import { useGetCarriersQuery } from '../../../redux/api/carriers/carrierApi';
import { CreateDriverData, Driver } from '../../../redux/api/driver/driver.model';
import { useGetDriversQuery } from '../../../redux/api/driver/driverApi';
import { Equipment, EquipmentType } from '../../../redux/api/equipment/equipment.model';
import { useSearchEquipmentsQuery } from '../../../redux/api/equipment/equipmentApi';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';

import {
  MIN_STOP_COUNT,
  SliceTrip,
  addCarrier,
  addDriver,
  addEquipment,
  addLoadDriver,
  deleteDriver,
  updateGrossPayEstimateData,
  updateSelectedCarrier,
  updateSelectedDriver,
  updateSelectedTrailer,
  updateSelectedTruck,
  updateOtherPayItemsData,
  updateTripInstructions,
  updateEstimatedMileageSegment,
  updateAutomaticMileage,
  selectIsManualLoadedMilesPresetByTripId
} from '../../../redux/features/orders/ordersSlice';
import { setAnchorId } from '../../../redux/features/navigation/navigationSlice';
import useScrollToView from '../../../hooks/useScrollToView';
import CloseIcon from '@mui/icons-material/Close';
import TwTimeline from '../../../components/TwTimeline/TwTimeline';
import TwCompaniesModal from '../../Companies/CompaniesModal/TwCompaniesModal';
import TwEditCompaniesModal from '../../Companies/EditCompaniesModal/TwEditCompaniesModal';
import { CompaniesEntities } from '../../Companies/Companies';
import Stack from '@mui/material/Stack';
import TwEmptyMiles from '../../../components/TwEmptyMiles/TwEmptyMiles';
import TwLoadedMiles from '../../../components/TwLoadedMiles/TwLoadedMiles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import './SplitDispatch.scss';
import {
  TripDriverInfo,
  TripDriverOtherPay,
  TripDriverOverrideData
} from '../../../redux/api/trips/trips.model';
import { EntityType } from '../../../redux/Global.model';
import { useGetTenantRoutingSettingsQuery } from '../../../redux/api/tenant/tenantApi';
import { PROVIDER } from '../../../providers/User/User.model';

interface SplitLoadSegmentProps {
  segment: SliceTrip;
  onSubmit?: () => void;
  totalLoadedMiles?: number;
  loading?: boolean;
  editMode?: boolean;
  isImport?: boolean;
  fullScreenImport?: boolean;
}

interface ContactFormDriver extends Partial<TripDriverInfo> {
  autocompleteDriver: TwAutocompleteOption | null | '';
}

const MAXIMUM_DRIVERS_ALLOWED = 2;

const TwDispatchTripFromOrder = forwardRef<HTMLDivElement, SplitLoadSegmentProps>(
  (
    { segment, onSubmit = (): void => undefined, loading, editMode, isImport, fullScreenImport },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const { getTenantSetting } = useAuth();

    const {
      order_id: load_id,
      trips: segments,
      estimated_mileage,
      duplicate_load
      //  provider_stops: loadStops
    } = useAppSelector((state) => state.orders);
    const anchorId = useAppSelector((state) => state.navigation.anchorId);
    const isEditLoadOpen = useAppSelector((state) => state.navigation.isEditLoadOpen);
    const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const driverRef = useRef<HTMLElement>(null);
    const [scrollToView] = useScrollToView();

    // const [initialStopOrder, setInitialStopOrder] = useState<number>(1);
    const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
    const [createCarrierDialogOpen, setCreateCarrierDialogOpen] = useState(false);
    const [createDriverDialogOpen, setCreateDriverDialogOpen] = useState(false);
    const [createEquipmentDialogOpen, setCreateEquipmentDialogOpen] = useState(false);
    const [createEquipmentType, setCreateEquipmentType] = useState<EquipmentType | undefined>(
      undefined
    );

    const [trailers, setTrailers] = useState<TwAutocompleteOption[]>([]);
    const [trucks, setTrucks] = useState<TwAutocompleteOption[]>([]);

    /* Edit dialogs states */
    const [selectedDriverId, setSelectedDriverId] = useState<string>('');
    const [editDriverOpen, setEditDriverOpen] = useState<boolean>(false);

    // Equipment to edit: can be truck or trailer so we manage the edition states centralized
    const [selectedEquipmentId, setSelectedEquipmentId] = useState<string>('');
    const [editEquipmentOpen, setEditEquipmentOpen] = useState<boolean>(false);

    const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
    const [editCarrierOpen, setEditCarrierOpen] = useState<boolean>(false);

    const [selectedTruck, setSelectedTruck] = useState<TwAutocompleteOption | null | undefined>(
      null
    );
    const [selectedTrailer, setSelectedTrailer] = useState<TwAutocompleteOption | null | undefined>(
      null
    );
    const [selectedCarrier, setSelectedCarrier] = useState<TwAutocompleteOption | null | undefined>(
      null
    );

    const [initialSpecialInstructions, setInitialSpecialInstructions] = useState<
      string | null | undefined
    >();
    const [specialInstructions, setSpecialInstructions] = useState<string | null | undefined>();

    // Team drivers
    const [currentDriverIndex, setCurrentDriverIndex] = useState<number>(0);
    const [currentDrivers, setCurrentDrivers] = useState<ContactFormDriver[]>([]);

    const [loadedMiles, setLoadedMiles] = useState<number | undefined>(undefined);
    const { enableDriverGrossPayEstimate, enableTeamDrivers, enablePcMiler } = useFeatureFlags();

    const carriersEnabled = getTenantSetting('multiple-carriers');
    const advancedDriverFieldsEnable = getTenantSetting('advanced-driver-fields');
    const isAppointmentWindowEnabled = getTenantSetting('enable-appointment-window');
    const isEmptyMilesEnabled = getTenantSetting('empty-miles');
    const isAutoCalculateMileageDisabled = getTenantSetting('disable-auto-calculate-mileage');

    const isManualLoadedMilesPresentForTrip = useAppSelector((state) =>
      selectIsManualLoadedMilesPresetByTripId(state, segment.ui_id)
    );

    const { data: drivers } = useGetDriversQuery(
      {},
      {
        selectFromResult: ({ data = { content: [], total: 0 } }) => ({
          data: {
            content: data.content.map((driver) => ({
              id: driver.id,
              label: `${driver.first_name} ${driver.last_name}`,
              subLabel:
                driver.phone && driver.email_address
                  ? `${StringUtils.formatPhoneNumber(driver.phone)} • ${driver.email_address}`
                  : driver.phone
                  ? StringUtils.formatPhoneNumber(driver.phone)
                  : driver.email_address
                  ? driver.email_address
                  : '',
              value: driver
            })),
            total: data.total
          }
        })
      }
    );

    const { data: equipmentData } = useSearchEquipmentsQuery(
      { params: { deleted: editMode ? true : false } },
      {
        selectFromResult: ({ data = { equipments: [], total: 0 } }) => ({
          data: {
            equipments: data.equipments.map((equipment) => ({
              id: equipment.id,
              label: equipment.name,
              value: equipment
            })),
            total: data.total
          }
        })
      }
    );

    // RTK Carrier
    const { data: carriers } = useGetCarriersQuery(
      {},
      {
        selectFromResult: ({ data = { content: [], total: 0 } }) => ({
          data: {
            content: data.content.map((carrier) => ({
              id: carrier.id,
              label: carrier.name,
              value: carrier.id
            })),
            total: data.total
          }
        })
      }
    );

    const { data: routingSettings } = useGetTenantRoutingSettingsQuery(null);

    // TODO: Check this
    useEffect(() => {
      equipmentData &&
        setTrailers(
          equipmentData.equipments.filter(
            (e) =>
              e.value.type === EquipmentType.trailer &&
              (!(e.value as Equipment).deleted_at ||
                (e.value as Equipment).id === segment.selected_trailer)
          )
        );
      equipmentData &&
        setTrucks(
          equipmentData.equipments.filter(
            (e) =>
              (e.value.type === EquipmentType.truck && !(e.value as Equipment).deleted_at) ||
              (e.value as Equipment).id === segment.selected_truck
          )
        );
    }, [JSON.stringify(equipmentData.equipments)]);

    useEffect(() => {
      const selectedTrailer = trailers.find((trailer) => trailer.id === segment.selected_trailer);
      setSelectedTrailer(selectedTrailer);
    }, [segment.selected_trailer, JSON.stringify(trailers?.map((t) => t.id))]);

    useEffect(() => {
      const selectedTruck = trucks.find((truck) => truck.id === segment.selected_truck);
      setSelectedTruck(selectedTruck);
    }, [segment.selected_truck, JSON.stringify(trucks?.map((t) => t.id))]);

    useEffect(() => {
      const selectedCarrier = carriers.content.find(
        (equip) => equip.id === segment.selected_carrier
      );
      setSelectedCarrier(selectedCarrier);
    }, [segment.selected_carrier, JSON.stringify(carriers.content)]);

    useEffect(() => {
      // if loaded miles is manual and has data already we don't override
      if (isManualLoadedMilesPresentForTrip || isAutoCalculateMileageDisabled) {
        return;
      }
      const segmentStops = [...segment.provider_stops];
      if (segmentStops.length >= MIN_STOP_COUNT) {
        const firstStop = segmentStops.shift();
        const lastStop = segmentStops.pop();
        if (firstStop?.provider_location && lastStop?.provider_location) {
          const intermediateStopsIds: string[] = [];
          segmentStops.map((stop) => {
            if (stop.provider_location) {
              intermediateStopsIds.push(stop.provider_location.id);
            }
          });

          LoadsService.getEstimatedMilage(
            firstStop.provider_location.id,
            lastStop.provider_location.id,
            intermediateStopsIds
          )
            .then((res) => {
              const mileage = Number(parseFloat(res.milage).toFixed(2));
              // Update the automatic mileage for the segment (which also updates the total automatic mileage for Adv rates miles calculator)
              dispatch(updateAutomaticMileage({ segmentId: segment.ui_id, mileage }));
              dispatch(
                updateEstimatedMileageSegment({
                  segmentId: segment.ui_id,
                  value: mileage,
                  is_auto: true,
                  source: !enablePcMiler
                    ? PROVIDER.GOOGLE
                    : routingSettings?.provider ?? PROVIDER.GOOGLE
                })
              );

              setLoadedMiles(mileage);
            })
            .catch((e) => {
              console.error(e);
            });
        }
      }
    }, [JSON.stringify(segment.provider_stops.map((s) => s.provider_location))]); // This way we catch changes in array items successfully

    useEffect(() => {
      const providerDrivers = [...segment.selected_drivers];
      const newCurrentDrivers: ContactFormDriver[] = providerDrivers.map((driver) => ({
        id: driver.id,
        driver_pay_override: driver.driver_pay_override,
        driver_pay_override_data: driver.driver_pay_override_data,
        send_driver_link: driver.send_driver_link,
        autocompleteDriver: drivers.content?.find((d) => d.id === driver.id) || null,
        trip_driver_pay_other_payment: driver.trip_driver_pay_other_payment
      }));
      setCurrentDrivers(newCurrentDrivers);
    }, [segment.selected_drivers, JSON.stringify(drivers.content.map((d) => d.id))]);

    useEffect(() => {
      if (load_id && !initialSpecialInstructions && !duplicate_load) {
        setInitialSpecialInstructions(segment.instructions);
        setSpecialInstructions(segment.instructions);
      }
    }, [load_id, segment.instructions]);

    useEffect(() => {
      if (isEditLoadOpen && anchorId && !loading) {
        if (anchorId === 'load-build-drivers-section') {
          scrollToView(driverRef, 'center');
          setTimeout(() => dispatch(setAnchorId(null)), 500);
        }
      }
    }, [anchorId, isEditLoadOpen, loading]);

    const handleEditDriverClose = (): void => {
      setEditDriverOpen(false);
      setSelectedDriverId('');
    };

    const handleEditEquipmentClose = (): void => {
      setEditEquipmentOpen(false);
      setSelectedEquipmentId('');
    };

    const handleEditCarrierClose = (): void => {
      setEditCarrierOpen(false);
      setSelectedCarrierId('');
    };

    const onDriverCreated = (
      newDriver: Driver,
      truckResponse?: Equipment,
      carrierResponse?: CarrierResponse,
      trailerResponse?: Equipment
    ): void => {
      const newAutocompleteOption: TwAutocompleteOption = {
        id: newDriver.id,
        label: `${newDriver.first_name} ${newDriver.last_name}`,
        value: newDriver
      };

      currentDrivers[currentDriverIndex] = {
        ...currentDrivers[currentDriverIndex],
        id: newDriver.id,
        autocompleteDriver: newAutocompleteOption
      };

      const { truckId, carrierId, trailerId } = maybeUpdateDriverTruckCarrierByDriver(
        truckResponse,
        carrierResponse,
        trailerResponse
      );

      dispatch(
        addDriver({
          params: {
            driver: newDriver,
            index: currentDriverIndex,
            selectedTruck: truckId,
            selectedCarrier: carrierId,
            selectedTrailer: trailerId
          },
          segmentOrder: segment.order
        })
      );
    };

    const onDriverEditSuccess = async (
      driverId: string,
      driverData?: CreateDriverData,
      selectedTruck?: Equipment,
      selectedCarrier?: CarrierResponse,
      selectedTrailer?: Equipment
    ): Promise<void> => {
      const driver = drivers.content?.find((d) => d.id === driverId);
      const index = currentDrivers.findIndex((d) => d.id === driver?.id);

      if (index !== -1 && driver) {
        currentDrivers[index] = {
          ...currentDrivers[index],
          autocompleteDriver: driver
        };

        const { truckId, carrierId, trailerId } = maybeUpdateDriverTruckCarrierByDriver(
          selectedTruck,
          selectedCarrier,
          selectedTrailer
        );

        dispatch(
          updateSelectedDriver({
            driver: {
              ...(currentDrivers[index] as TripDriverInfo),
              driver_pay_override: undefined,
              driver_pay_override_data: undefined
            },
            index,
            segmentOrder: segment.order,
            selectedTruck: truckId,
            selectedCarrier: carrierId,
            selectedTrailer: trailerId
          })
        );
      }
    };

    /**
     * Helper function to maybe set truck, carrier, and trailer from driver data  when a driver is created or
     * edited only if load is being created or if an existing load and that object is not already set
     *
     * @param truckResponse
     * @param carrierResponse
     * @param trailerResponse
     * */
    const maybeUpdateDriverTruckCarrierByDriver = (
      truckResponse?: Equipment,
      carrierResponse?: CarrierResponse,
      trailerResponse?: Equipment
    ): {
      truckId: string | undefined;
      carrierId: string | undefined;
      trailerId: string | undefined;
    } => {
      const isNewLoad = !load_id;
      let truckId: string | undefined = undefined;
      let carrierId: string | undefined = undefined;
      let trailerId: string | undefined = undefined;
      // only need to set if it's a new load or if no truck is selected
      if ((isNewLoad || !selectedTruck) && truckResponse) {
        const newTruck = trucks.find((e) => e.id === truckResponse.id);
        truckId = newTruck?.id;

        dispatch(
          updateSelectedTruck({
            segmentOrder: segment.order,
            value: truckId,
            preserveEmptyMiles: isAutoCalculateMileageDisabled
          })
        );
      }

      // only need to set if it's a new load or if no trailer is selected
      if ((isNewLoad || !selectedTrailer) && trailerResponse) {
        const newTrailer = trailers.find((e) => e.id === trailerResponse.id);
        trailerId = newTrailer?.id;

        dispatch(
          updateSelectedTrailer({
            segmentOrder: segment.order,
            value: trailerId
          })
        );
      }

      // only need to set if it's a new load or if no carrier is selected
      if ((isNewLoad || !selectedCarrier) && carrierResponse && carriersEnabled) {
        const newCarrier = carriers.content.find((c) => c.id === carrierResponse.id);

        carrierId = newCarrier?.id;

        dispatch(
          updateSelectedCarrier({
            segmentOrder: segment.order,
            value: carrierId
          })
        );
      }

      return { truckId, carrierId, trailerId };
    };

    // Lists the driver options for the Driver 1
    const driversForOption1 = React.useMemo(() => {
      if (currentDrivers.length > 1) {
        return drivers.content?.filter((driver) => driver.id !== currentDrivers[1].id) || [];
      }

      return drivers.content || [];
    }, [drivers, currentDrivers]);

    // Lists the driver options for the Driver 2
    const driversForOption2 = React.useMemo(() => {
      if (currentDrivers.length > 0) {
        return drivers.content?.filter((driver) => driver.id !== currentDrivers[0].id) || [];
      }

      return drivers.content || [];
    }, [drivers, currentDrivers]);

    const onDriverSelected = ({
      driver,
      index,
      autocompleteDriver,
      truckId,
      carrierId,
      trailerId
    }: {
      driver: TripDriverInfo;
      index: number;
      autocompleteDriver: TwAutocompleteOption;
      truckId?: string;
      carrierId?: string;
      trailerId?: string;
    }): void => {
      const newCurrentDriver: ContactFormDriver = {
        id: autocompleteDriver.id,
        autocompleteDriver: autocompleteDriver,
        send_driver_link: false
      };

      dispatch(
        updateSelectedDriver({
          driver: {
            ...driver,
            driver_pay_override: undefined,
            driver_pay_override_data: undefined,
            trip_driver_pay_other_payment:
              (autocompleteDriver.value as TripDriverInfo).trip_driver_pay_other_payment || []
          },
          index,
          segmentOrder: segment.order,
          selectedTruck: truckId,
          selectedCarrier: carrierId,
          selectedTrailer: trailerId
        })
      );

      currentDrivers[index] = newCurrentDriver;

      setCurrentDrivers(currentDrivers);
    };

    const onEquipmentCreated = (newEquipment: Equipment): void => {
      dispatch(
        addEquipment({
          equipment: {
            id: newEquipment.id,
            type: newEquipment.type
          },
          segmentOrder: segment.order
        })
      );

      if (newEquipment.type === 'TRUCK') {
        dispatch(
          updateSelectedTruck({
            segmentOrder: segment.order,
            value: newEquipment.id,
            preserveEmptyMiles: isAutoCalculateMileageDisabled
          })
        );
      }
      if (newEquipment.type === 'TRAILER') {
        dispatch(
          updateSelectedTrailer({
            segmentOrder: segment.order,
            value: newEquipment.id
          })
        );
      }
    };

    const onCarrierCreated = (newCarrier: CarrierResponse): void => {
      dispatch(
        addCarrier({
          carrierId: newCarrier.id,
          segmentOrder: segment.order
        })
      );
    };

    const getEditTooltipText = (inputName: string): string => {
      return `Editing selected ${inputName} will impact all loads where the ${inputName} is used`;
    };

    const openEditDialog = (inputName: string, index?: number): void => {
      switch (inputName) {
        case 'driver':
          if (index !== undefined && !!currentDrivers[index].id) {
            setSelectedDriverId(currentDrivers[index].id as string);
            setEditDriverOpen(true);
          }
          break;
        case 'carrier':
          if (!!selectedCarrier) {
            setSelectedCarrierId(selectedCarrier.id);
            setEditCarrierOpen(true);
          }
          break;
        case 'truck':
          if (!!selectedTruck) {
            setSelectedEquipmentId(selectedTruck.id);
            setEditEquipmentOpen(true);
          }
          break;
        case 'trailer':
          if (!!selectedTrailer) {
            setSelectedEquipmentId(selectedTrailer.id);
            setEditEquipmentOpen(true);
          }
          break;
      }
    };

    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Enter') {
        if (e.currentTarget.outerHTML.includes('driver')) {
          setCreateDriverDialogOpen(true);
        }
        if (e.currentTarget.outerHTML.includes('carrier')) {
          setCreateCarrierDialogOpen(true);
        }
        if (e.currentTarget.outerHTML.includes('trailer')) {
          setCreateEquipmentType(EquipmentType.trailer);
          setCreateEquipmentDialogOpen(true);
        }
        if (e.currentTarget.outerHTML.includes('truck')) {
          setCreateEquipmentType(EquipmentType.truck);
          setCreateEquipmentDialogOpen(true);
        }
      }
    };

    return (
      <>
        <Box ref={ref} component="div" className="segment__wrapper tid-segment__wrapper">
          <div className="segment__form-section">
            <span className="segment__title">{`Trip ${segment.order} of ${segments.length}`}</span>
            <span className="segment__instructions">Provide information about this trip </span>

            <Stack
              flexDirection={
                (isImport && !fullScreenImport) || matchesMobileOrTablet ? 'column' : 'row'
              }
              className="segment__data-container"
            >
              <Stack
                className="segment__route-container-stack"
                sx={
                  (isImport && !fullScreenImport) || matchesMobileOrTablet
                    ? { order: '1 !important', width: '100% !important' }
                    : {}
                }
              >
                <div className="segment__route-container">
                  <div className="segment__route-details">
                    <TwTimeline stepStartNumber={segment.provider_stops[0].order}>
                      {segment.provider_stops.map((stop) => (
                        <Box
                          key={`segment-${segment.order}-stop-${stop.order}`}
                          sx={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}
                        >
                          <span className="segment__route-location-title">
                            {stop.provider_location?.label}
                          </span>

                          <span className="segment__route-location-address">
                            {stop.location?.address}
                          </span>

                          <Box component="span" className="segment__route-date-time">
                            {stop.provider_date &&
                              DateUtils.getMediumDate(new Date(stop.provider_date))}
                            {stop.provider_time_from && ` • `}
                            {stop.provider_time_from
                              ? DateUtils.getTime(stop.provider_time_from)
                              : ''}
                            {isAppointmentWindowEnabled && stop.provider_time_to && ` - `}
                            {isAppointmentWindowEnabled &&
                              stop.provider_time_to &&
                              DateUtils.getTime(stop.provider_time_to)}
                          </Box>

                          <Box className="segment__route-contact">
                            <span>
                              {stop.provider_stop_type ? `${stop.provider_stop_type.label} ` : ''}
                            </span>
                            <span>{stop.stop_identifier ? `#${stop.stop_identifier}` : ''}</span>
                          </Box>

                          {stop.contact?.contact_name && (
                            <Box className="segment__route-contact">
                              <p>Contact:</p>
                              <span>{stop.contact.contact_name}</span>
                              <span className="contact-info">{`${
                                stop.contact.contact_number
                                  ? StringUtils.formatPhoneNumber(
                                      stop?.contact?.contact_number,
                                      stop?.contact?.contact_number_ext
                                    )
                                  : ''
                              } ${
                                stop.contact.contact_email ? stop.contact.contact_email : ''
                              }`}</span>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </TwTimeline>
                  </div>
                </div>
              </Stack>
              <Stack
                flexDirection={'column'}
                className="segment__route-inputs"
                sx={
                  (isImport && !fullScreenImport) || matchesMobileOrTablet
                    ? { order: '2 !important', width: '100% !important' }
                    : {}
                }
              >
                <Box
                  className="tid-segment__driver segment__driver"
                  ref={segment.order === 1 ? driverRef : null}
                >
                  {currentDrivers.map((driver, index) => (
                    <React.Fragment key={index}>
                      <div className="segment__driver-edit">
                        <TwAutocomplete
                          name={`driver-${index + 1}`}
                          label={currentDrivers.length > 1 ? `Driver ${index + 1}` : 'Driver'}
                          optionList={index === 0 ? driversForOption1 : driversForOption2}
                          renderOption={(props, option): React.ReactNode => {
                            return <RenderOption props={props} option={option} />;
                          }}
                          value={currentDrivers[index].autocompleteDriver || ''}
                          onChange={(
                            _,
                            newValue: TwAutocompleteOption | null
                          ): Promise<void> | undefined => {
                            let truckId: string | undefined = undefined;
                            let carrierId: string | undefined = undefined;
                            let trailerId: string | undefined = undefined;

                            if (newValue === null) {
                              dispatch(
                                updateSelectedDriver({
                                  driver: {
                                    id: '',
                                    driver_pay_override: undefined,
                                    driver_pay_override_data: undefined
                                  },
                                  index,
                                  segmentOrder: segment.order
                                })
                              );

                              currentDrivers[index] = { id: '', autocompleteDriver: null };

                              setCurrentDrivers(currentDrivers);
                            }

                            if (newValue?.value) {
                              if (index === 0) {
                                const newTruck = (newValue.value as Driver).default_truck
                                  ? trucks.find(
                                      (e) => e.id === (newValue.value as Driver).default_truck?.id
                                    )
                                  : trucks.find(
                                      (e) => e.id === (newValue.value as Driver).default_truck_id
                                    );

                                // we only want to set the driver truck if creating a new load or if no truck is selected for existing load
                                if (newTruck && !selectedTruck) {
                                  truckId = newTruck?.id;

                                  dispatch(
                                    updateSelectedTruck({
                                      segmentOrder: segment.order,
                                      value: truckId,
                                      preserveEmptyMiles: isAutoCalculateMileageDisabled
                                    })
                                  );
                                }

                                // only want to set the carrier if creating a new load or if no carrier is selected for existing load
                                if (carriersEnabled && !selectedCarrier) {
                                  const newCarrier = carriers.content.find(
                                    (c) => c.id === (newValue.value as Driver).carrier?.id
                                  );

                                  carrierId = newCarrier?.id;

                                  dispatch(
                                    updateSelectedCarrier({
                                      segmentOrder: segment.order,
                                      value: carrierId || ''
                                    })
                                  );
                                }

                                const newTrailer = (newValue.value as Driver).default_trailer
                                  ? trailers.find(
                                      (e) => e.id === (newValue.value as Driver).default_trailer?.id
                                    )
                                  : trailers.find(
                                      (e) => e.id === (newValue.value as Driver).default_trailer_id
                                    );

                                if (newTrailer && !selectedTrailer) {
                                  trailerId = newTrailer?.id;

                                  dispatch(
                                    updateSelectedTrailer({
                                      segmentOrder: segment.order,
                                      value: trailerId
                                    })
                                  );
                                }
                              }

                              const newDriver = { ...driver } as any;
                              delete newDriver.autocompleteDriver;

                              onDriverSelected({
                                driver: {
                                  ...(newDriver as TripDriverInfo),
                                  id: newValue.id,
                                  is_primary: index === 0
                                },
                                autocompleteDriver: newValue,
                                index,
                                truckId,
                                carrierId,
                                trailerId
                              });

                              return undefined;
                            }
                          }}
                          listAction={{
                            label: 'New driver',
                            action: (): void => {
                              setCurrentDriverIndex(index);
                              setCreateDriverDialogOpen(true);
                            }
                          }}
                          disableNoOption
                          enableClearIcon
                          onKeyDown={onKeyDownHandler}
                        />

                        {!!driver.id && !!driver.autocompleteDriver && (
                          <TwTooltip title={getEditTooltipText('driver')} placement="top">
                            <EditIcon
                              className="contact-details__edit-icon"
                              onClick={(): void => openEditDialog('driver', index)}
                            />
                          </TwTooltip>
                        )}

                        {currentDrivers.length > 1 && (
                          <CloseIcon
                            className="segment__driver-close"
                            onClick={(): void => {
                              dispatch(
                                deleteDriver({
                                  segmentOrder: segment.order,
                                  driverOrder: index + 1
                                })
                              );
                            }}
                          />
                        )}
                      </div>

                      {advancedDriverFieldsEnable && !!driver.id && enableDriverGrossPayEstimate && (
                        <TwDriverPayCalculator
                          entity={EntityType.ORDER}
                          driver={driver}
                          loadedMiles={loadedMiles || 0}
                          totalLoadedMiles={estimated_mileage || 0}
                          onGrossPayEstimateDataSave={(
                            grossPayEstimateData: TripDriverOverrideData | undefined
                          ): void => {
                            if (!grossPayEstimateData) return;

                            dispatch(
                              updateGrossPayEstimateData({
                                driverIndex: index,
                                grossPayEstimateData,
                                segmentOrder: segment.order
                              })
                            );
                          }}
                          onOtherLoadPayItemsSave={(
                            otherLoadPayItems: TripDriverOtherPay[]
                          ): void => {
                            dispatch(
                              updateOtherPayItemsData({
                                driverIndex: index,
                                otherPayItemsData: otherLoadPayItems,
                                segmentOrder: segment.order
                              })
                            );
                          }}
                          handleOpenEditDriver={(): void => openEditDialog('driver', index)}
                          splitLoadSegment={segment}
                          driversNumber={currentDrivers.length}
                          tripLevelLoadedMiles={segment.estimated_mileage || 0}
                          tripLevelEmptyMiles={segment.empty_miles || 0}
                        />
                      )}
                    </React.Fragment>
                  ))}

                  {segment.selected_drivers.length < MAXIMUM_DRIVERS_ALLOWED &&
                    enableTeamDrivers &&
                    currentDrivers.every((d) => !!d.id) && (
                      <Box
                        component="div"
                        onClick={(): void => {
                          dispatch(
                            addLoadDriver({
                              segmentOrder: segment.order,
                              callback: () => null
                            })
                          );
                        }}
                        sx={{
                          color: '#3A4DE9',
                          fontSize: '14px',
                          cursor: 'pointer',
                          paddingTop: '16px',
                          marginBottom: '16px'
                        }}
                      >
                        + Add another driver
                      </Box>
                    )}
                </Box>

                {carriersEnabled && (
                  <div className="tid-segment__carrier segment__carrier">
                    <div className="segment__carrier-edit">
                      <TwAutocomplete
                        id="carrier"
                        name="carrier"
                        label="Carrier"
                        optionList={carriers.content}
                        value={selectedCarrier || ''}
                        onChange={(_, newValue: TwAutocompleteOption | null): void => {
                          dispatch(
                            updateSelectedCarrier({
                              segmentOrder: segment.order,
                              value: newValue?.id
                            })
                          );
                        }}
                        listAction={{
                          label: 'New carrier',
                          action: (): void => setCreateCarrierDialogOpen(true)
                        }}
                        disableNoOption={true}
                        enableClearIcon
                        onKeyDown={onKeyDownHandler}
                      />
                      {selectedCarrier && (
                        <TwTooltip title={getEditTooltipText('carrier')} placement="top">
                          <EditIcon
                            className="segment__edit-icon"
                            onClick={(): void => openEditDialog('carrier')}
                          />
                        </TwTooltip>
                      )}
                    </div>
                  </div>
                )}

                <Box className="tid-segment__equipment segment__equipment">
                  <div className="segment__truck-edit">
                    <TwAutocomplete
                      id="truck"
                      name="truck"
                      label="Truck"
                      optionList={trucks}
                      value={selectedTruck || ''}
                      onChange={(_, newValue: TwAutocompleteOption | null): void => {
                        dispatch(
                          updateSelectedTruck({
                            segmentOrder: segment.order,
                            value: newValue?.id,
                            preserveEmptyMiles: isAutoCalculateMileageDisabled
                          })
                        );
                      }}
                      listAction={{
                        label: 'New truck',
                        action: (): void => {
                          setCreateEquipmentType(EquipmentType.truck);
                          setCreateEquipmentDialogOpen(true);
                        }
                      }}
                      disableNoOption={true}
                      enableClearIcon
                      onKeyDown={onKeyDownHandler}
                      sx={selectedTruck ? { flex: 1 } : { flex: 1, marginRight: '12px' }}
                    />
                    {selectedTruck && !(selectedTruck?.value as Equipment)?.deleted_at && (
                      <TwTooltip title={getEditTooltipText('truck')} placement="top">
                        <EditIcon
                          className="segment__edit-icon"
                          onClick={(): void => openEditDialog('truck')}
                        />
                      </TwTooltip>
                    )}
                  </div>

                  <div className="segment__trailer-edit">
                    <TwAutocomplete
                      id="trailer"
                      name="trailer"
                      label="Trailer"
                      optionList={trailers}
                      value={selectedTrailer || ''}
                      onChange={(_, newValue: TwAutocompleteOption | null): void => {
                        dispatch(
                          updateSelectedTrailer({
                            segmentOrder: segment.order,
                            value: newValue?.id
                          })
                        );
                      }}
                      listAction={{
                        label: 'New trailer',
                        action: (): void => {
                          setCreateEquipmentType(EquipmentType.trailer);
                          setCreateEquipmentDialogOpen(true);
                        }
                      }}
                      disableNoOption={true}
                      enableClearIcon
                      onKeyDown={onKeyDownHandler}
                      sx={{ flex: 1 }}
                    />
                    {selectedTrailer && !(selectedTrailer?.value as Equipment)?.deleted_at && (
                      <TwTooltip title={getEditTooltipText('trailer')} placement="top">
                        <EditIcon
                          className="segment__edit-icon"
                          onClick={(): void => openEditDialog('trailer')}
                        />
                      </TwTooltip>
                    )}
                  </div>
                </Box>
                <Stack flexDirection={'row'} gap={2}>
                  {isEmptyMilesEnabled && (
                    <TwEmptyMiles
                      segmentId={segment.id}
                      firstStop={segment.provider_stops[0] || null}
                      truck={selectedTruck || null}
                      initialEmptyMiles={segment.empty_miles}
                      initialAutoCalculate={segment.is_empty_miles_auto}
                      initialSource={segment.empty_mileage_source || null}
                      segmentUid={segment.ui_id}
                      entity={EntityType.ORDER}
                    />
                  )}
                  <TwLoadedMiles
                    stops={segment.provider_stops}
                    segmentId={segment.ui_id}
                    initialLoadedMiles={segment.estimated_mileage}
                    initialAutoCalculate={segment.is_loaded_miles_auto}
                    initialSource={segment.loaded_mileage_source || null}
                    initialStopOrder={segment.provider_stops[0].order}
                    entity={EntityType.ORDER}
                  />
                </Stack>

                <div className="tid-segment__special-instructions">
                  <TwField
                    id="specialInstructions"
                    name="specialInstructions"
                    label="Trip notes"
                    inputType={TwInputTypes.textareaAuto}
                    value={specialInstructions || initialSpecialInstructions || ''}
                    onInputBlur={(e): void => {
                      segment.sort_order &&
                        dispatch(
                          updateTripInstructions({
                            segmentOrder: segment.sort_order,
                            instructions: e.target.value
                          })
                        );
                    }}
                    onInputChange={(e): void => {
                      if (initialSpecialInstructions) setInitialSpecialInstructions(undefined);
                      setSpecialInstructions(e.target.value);
                    }}
                  />
                </div>
              </Stack>
            </Stack>
          </div>
        </Box>

        <CreateEquipment
          open={createEquipmentDialogOpen}
          defaultType={createEquipmentType}
          onClose={(): void => setCreateEquipmentDialogOpen(false)}
          onSuccess={onEquipmentCreated}
        />
        <CreateDriver
          open={createDriverDialogOpen}
          onClose={(): void => setCreateDriverDialogOpen(false)}
          onSuccess={onDriverCreated}
          onCarrierCreated={(newCarrier): void => {
            dispatch(
              addCarrier({
                carrierId: newCarrier.id,
                segmentOrder: segment.order,
                abortUpdate: true
              })
            );
          }}
          onEquipmentCreated={(newEquipment): void => {
            dispatch(
              addEquipment({
                equipment: {
                  id: newEquipment.id,
                  type: newEquipment.type
                },
                segmentOrder: segment.order,
                abortUpdate: true
              })
            );
          }}
        />

        <EditDriver
          driverId={selectedDriverId}
          open={editDriverOpen}
          onClose={handleEditDriverClose}
          onSuccess={onDriverEditSuccess}
          onCarrierCreated={(newCarrier): void => {
            dispatch(
              addCarrier({
                carrierId: newCarrier.id,
                segmentOrder: segment.order,
                abortUpdate: true
              })
            );
          }}
          onEquipmentCreated={(newEquipment): void => {
            dispatch(
              addEquipment({
                equipment: {
                  id: newEquipment.id,
                  type: newEquipment.type
                },
                segmentOrder: segment.order,
                abortUpdate: true
              })
            );
          }}
        />

        {/* Edit dialog for truck and trailer */}
        <EditEquipment
          equipmentId={selectedEquipmentId}
          open={editEquipmentOpen}
          onClose={handleEditEquipmentClose}
        />

        <TwCompaniesModal
          open={createCarrierDialogOpen}
          entity={CompaniesEntities.carrier}
          onClose={(): void => setCreateCarrierDialogOpen(false)}
          onSuccess={onCarrierCreated}
        />
        <TwEditCompaniesModal
          open={editCarrierOpen}
          entity={CompaniesEntities.carrier}
          onClose={handleEditCarrierClose}
          entityId={selectedCarrierId}
        />
      </>
    );
  }
);

TwDispatchTripFromOrder.displayName = 'TwDispatchTripFromOrder';

export default TwDispatchTripFromOrder;
